import { getAuth, getIdToken } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  onSnapshot,
  FirestoreError,
} from "firebase/firestore";
import axios from "axios";
import {
  ExistingEmailTemplate,
  EmailTemplateManager,
} from "../models/email-template";
import {
  ExistingEmailCampaign,
  EmailCampaignManager,
} from "../models/email-campaign";
import { logger } from "../logging";
import { ExistingCustomer } from "../models/customer";
import { ExistingSentEmail, SentEmailManager } from "../models/sent-email";
import { CSVCustomerRecord } from "../components/EmailMarketing/CSVCustomerUploader";

// Interface for email campaign statistics
export interface EmailCampaignStatistics {
  sent: number;
  bounced: number;
  spamComplaints: number;
  tracked: number;
  opens: number;
  uniqueOpens: number;
  clicked: number;
  uniqueClicked: number;
  withClientRecorded: number;
  withPlatformRecorded: number;
  withReadTimeRecorded: number;
}

// Interface for API request
export interface APICreateAndSendEmailCampaignRequest {
  siteKey: string;
  templateID: string;
  campaignName: string;
  description?: string;
  emails: Array<{
    customerID?: string;
    customerName: string;
    emailAddress: string;
    templateData?: Record<string, any>;
  }>;
}

// Interface for API response
export interface APICreateAndSendEmailCampaignResponse {
  campaignID: string;
  totalRecipients: number;
  status: string;
}

const projectName = import.meta.env.VITE_APP_FIREBASE_PROJECT;
let apiBaseURL = `https://us-central1-${projectName}.cloudfunctions.net/api`;
if (import.meta.env.VITE_APP_USE_FUNCTIONS_EMULATOR === "true") {
  apiBaseURL = `http://localhost:5001/${projectName}/us-central1/api`;
}

/**
 * Create a new email template
 */
export async function createEmailTemplate(args: {
  siteKey: string;
  name: string;
  description?: string;
  htmlContent: string;
  subject: string;
  status?: string;
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-templates";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, args, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Update an existing email template
 */
export async function updateEmailTemplate(args: {
  siteKey: string;
  templateID: string;
  name?: string;
  description?: string;
  htmlContent?: string;
  subject?: string;
  status?: string;
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-templates";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.put(fullApiRoute, args, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Delete an email template
 */
export async function deleteEmailTemplate(args: {
  siteKey: string;
  templateID: string;
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-templates";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.delete(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
      data: args,
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Get all email templates for a site
 */
export async function getAllEmailTemplates(
  siteKey: string,
): Promise<ExistingEmailTemplate[]> {
  const db = getFirestore();
  const templatesRef = collection(db, "siteKeys", siteKey, "emailTemplates");
  const q = query(templatesRef, where("deleted", "==", false));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    EmailTemplateManager.createFromSnapshot(doc),
  );
}

/**
 * Get a single email template by ID
 */
export async function getEmailTemplate(
  siteKey: string,
  templateID: string,
): Promise<ExistingEmailTemplate> {
  const db = getFirestore();
  const templateDoc = await getDoc(
    doc(db, "siteKeys", siteKey, "emailTemplates", templateID),
  );

  if (!templateDoc.exists()) {
    throw new Error(`Email template not found: ${templateID}`);
  }

  return EmailTemplateManager.createFromSnapshot(templateDoc);
}

/**
 * Subscribe to all email templates for a site
 */
export function subscribeEmailTemplates(args: {
  siteKey: string;
  onChange: (templates: ExistingEmailTemplate[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const templatesRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "emailTemplates",
  );
  const q = query(templatesRef, where("deleted", "==", false));

  return onSnapshot(
    q,
    (querySnapshot) => {
      const templates = querySnapshot.docs.map((doc) =>
        EmailTemplateManager.createFromSnapshot(doc),
      );
      args.onChange(templates);
    },
    (error) => {
      if (args.onError) {
        args.onError(error);
      } else {
        console.error("Error subscribing to email templates:", error);
      }
    },
  );
}

/**
 * Create a new email campaign
 */
export async function createEmailCampaign(args: {
  siteKey: string;
  templateID: string;
  campaignName: string;
  description?: string;
  customers?: ExistingCustomer[];
  csvData?: Array<{
    email: string;
    customer_name?: string;
    customerID?: string;
    rowNumber: number;
  }>;
}): Promise<APICreateAndSendEmailCampaignResponse> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  // Transform the data to match the new API format
  const emails: APICreateAndSendEmailCampaignRequest["emails"] = [];

  // Add existing customers to emails array
  if (args.customers && args.customers.length > 0) {
    for (const customer of args.customers) {
      // Get primary email address
      const primaryEmail =
        customer.email && customer.email.length > 0 ? customer.email[0] : null;

      if (primaryEmail) {
        // Determine customer name based on type
        let customerName = customer.name;
        if (
          customer.type === "residential" &&
          customer.firstName &&
          customer.lastName
        ) {
          customerName = `${customer.firstName} ${customer.lastName}`;
        }

        emails.push({
          customerID: customer.id,
          customerName: customerName,
          emailAddress: primaryEmail,
          templateData: {
            customerName: customerName,
            // Add any other template variables you want to support
          },
        });
      }
    }
  }

  // Add CSV data to emails array
  if (args.csvData && args.csvData.length > 0) {
    for (const csvRecord of args.csvData) {
      emails.push({
        customerID: csvRecord.customerID,
        customerName: csvRecord.customer_name || "Valued Customer",
        emailAddress: csvRecord.email,
        templateData: {
          customerName: csvRecord.customer_name || "Valued Customer",
        },
      });
    }
  }

  const requestBody: APICreateAndSendEmailCampaignRequest = {
    siteKey: args.siteKey,
    templateID: args.templateID,
    campaignName: args.campaignName,
    description: args.description,
    emails: emails,
  };

  const endpoint = "email-campaigns";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    const response = await axios.post<APICreateAndSendEmailCampaignResponse>(
      fullApiRoute,
      requestBody,
      {
        headers: { authorization: `Bearer ${idToken}` },
      },
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw new Error(
        err.response?.data?.message || "Failed to create email campaign",
      );
    }
    throw err;
  }
}

/**
 * Cancel an email campaign
 */
export async function cancelEmailCampaign(args: {
  siteKey: string;
  campaignID: string;
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-campaigns/cancel";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, args, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Get all email campaigns for a site
 */
export async function getAllEmailCampaigns(
  siteKey: string,
): Promise<ExistingEmailCampaign[]> {
  const db = getFirestore();
  const campaignsRef = collection(db, "siteKeys", siteKey, "emailCampaigns");
  const q = query(campaignsRef, where("deleted", "==", false));

  const querySnapshot = await getDocs(q);
  const campaigns = querySnapshot.docs.map((doc) =>
    EmailCampaignManager.createFromSnapshot(doc),
  );

  // Sort by timestampCreated descending in the browser
  return campaigns.sort(
    (a, b) => b.timestampCreated.toMillis() - a.timestampCreated.toMillis(),
  );
}

/**
 * Get a single email campaign by ID
 */
export async function getEmailCampaign(
  siteKey: string,
  campaignID: string,
): Promise<ExistingEmailCampaign> {
  const db = getFirestore();
  const campaignDoc = await getDoc(
    doc(db, "siteKeys", siteKey, "emailCampaigns", campaignID),
  );

  if (!campaignDoc.exists()) {
    throw new Error(`Email campaign not found: ${campaignID}`);
  }

  return EmailCampaignManager.createFromSnapshot(campaignDoc);
}

/**
 * Subscribe to all email campaigns for a site
 */
export function subscribeEmailCampaigns(args: {
  siteKey: string;
  onChange: (campaigns: ExistingEmailCampaign[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const campaignsRef = collection(
    db,
    "siteKeys",
    args.siteKey,
    "emailCampaigns",
  );
  const q = query(campaignsRef, where("deleted", "==", false));

  return onSnapshot(
    q,
    (querySnapshot) => {
      const campaigns = querySnapshot.docs.map((doc) =>
        EmailCampaignManager.createFromSnapshot(doc),
      );

      // Sort by timestampCreated descending in the browser
      const sortedCampaigns = campaigns.sort(
        (a, b) => b.timestampCreated.toMillis() - a.timestampCreated.toMillis(),
      );
      args.onChange(sortedCampaigns);
    },
    (error) => {
      logger.error("Error subscribing to email campaigns:", error);
      if (args.onError) {
        args.onError(error);
      }
    },
  );
}

/**
 * Get statistics for an email campaign from Postmark
 */
export async function getEmailCampaignStatistics(args: {
  siteKey: string;
  campaignId: string;
}): Promise<EmailCampaignStatistics> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-campaigns/stats";
  const fullApiRoute = `${apiBaseURL}/${endpoint}?campaignId=${args.campaignId}&siteKey=${args.siteKey}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: { authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Send a test email with a template
 */
export async function sendTestEmail(args: {
  templateId: string;
  siteKey: string;
  emailAddress: string;
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "email-templates/test-email";
  const fullApiRoute = `${apiBaseURL}/${endpoint}`;

  try {
    await axios.post(fullApiRoute, args, {
      headers: { authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.info(err.response?.data);
      logger.info(err.response?.status);
      logger.info(err.response?.headers);
      throw err;
    }
    throw err;
  }
}

/**
 * Get all sent emails for a specific campaign
 */
export async function getSentEmailsByCampaign(
  siteKey: string,
  campaignID: string,
): Promise<ExistingSentEmail[]> {
  const db = getFirestore();
  const sentEmailsRef = collection(db, "siteKeys", siteKey, "sentEmails");
  const q = query(sentEmailsRef, where("campaignID", "==", campaignID));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    SentEmailManager.createFromSnapshot(doc),
  );
}

/**
 * Get all sent emails for a specific campaign (alias for consistency with database naming)
 */
export async function getSentEmailsByCampaignID(
  siteKey: string,
  campaignID: string,
): Promise<ExistingSentEmail[]> {
  return getSentEmailsByCampaign(siteKey, campaignID);
}

/**
 * Subscribe to sent emails for a specific campaign
 */
export function subscribeSentEmailsByCampaign(args: {
  siteKey: string;
  campaignID: string;
  onChange: (sentEmails: ExistingSentEmail[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const db = getFirestore();
  const sentEmailsRef = collection(db, "siteKeys", args.siteKey, "sentEmails");
  const q = query(sentEmailsRef, where("campaignID", "==", args.campaignID));

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const sentEmails = querySnapshot.docs.map((doc) =>
        SentEmailManager.createFromSnapshot(doc),
      );
      args.onChange(sentEmails);
    },
    args.onError,
  );

  return unsubscribe;
}

/**
 * Subscribe to sent emails for a specific campaign (alias for consistency with database naming)
 */
export function subscribeSentEmailsByCampaignID(args: {
  siteKey: string;
  campaignID: string;
  onChange: (sentEmails: ExistingSentEmail[]) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  return subscribeSentEmailsByCampaign(args);
}

// Add single email campaign subscription function
export function subscribeEmailCampaign(args: {
  siteKey: string;
  campaignID: string;
  onChange: (campaign: ExistingEmailCampaign) => void;
  onError?: (error: FirestoreError) => void;
}): () => void {
  const { siteKey, campaignID, onChange, onError } = args;

  const db = getFirestore();
  const collectionRef = collection(db, `siteKeys/${siteKey}/emailCampaigns`);
  const docRef = doc(collectionRef, campaignID);

  return onSnapshot(
    docRef,
    (snapshot) => {
      try {
        if (!snapshot.exists()) {
          throw new Error(`Email campaign ${campaignID} not found`);
        }
        const campaign = EmailCampaignManager.createFromSnapshot(snapshot);
        onChange(campaign);
      } catch (error) {
        console.error("Error in subscribeEmailCampaign:", error);
        if (onError) {
          onError(error as FirestoreError);
        }
      }
    },
    (error) => {
      console.error("Error subscribing to email campaign:", error);
      if (onError) {
        onError(error);
      }
    },
  );
}
